/**
 * TuonoScreen — "Massima sicurezza ⚡" (Premium). Pacchetto della postura più
 * protetta: inbox APERTA (acceptMode 'open') + setup Tor + guida a un'identità
 * usata solo via Tor. Onesto sui costi (Tor lento, inbox esposta, identità da
 * ricominciare). L'auto-creazione di un sigil Tor-only è una v1.5 (serve il
 * multi-identità: oggi Nullum tiene una identità per dispositivo).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { useStore } from '../lib/store';
import { setAcceptMode } from '../lib/messaging';

export default function TuonoScreen() {
  const navigate = useNavigate();
  const { premium, isFounder, premiumLifetime, acceptMode: storeMode } = useStore();
  const hasPremium = premium || isFounder || premiumLifetime;
  const [mode, setMode] = useState(storeMode || 'request');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function applyMode(m) {
    setBusy(true); setMsg(null);
    try {
      await setAcceptMode(m);
      setMode(m);
      setMsg(m === 'open' ? '✅ Inbox aperta attiva' : 'Inbox riportata a "richiesta".');
    } catch (e) {
      setMsg('errore: ' + (e?.message || e));
    } finally { setBusy(false); }
  }

  if (!hasPremium) {
    return (
      <Frame statusLeft="TUONO" statusRight="⚡">
        <Header title="Massima sicurezza ⚡" crumb="◀" onCrumbClick={() => navigate('/sicurezza')} />
        <Box variant="warn">
          <p className="text-[15px]">⚡ <b>Massima sicurezza</b> è una funzione <b>Premium</b>.</p>
          <p className="text-[13px] text-ink-dim mt-2">
            La protezione più forte di Nullum: nasconde da dove ti colleghi e apre l'inbox a tutti.
          </p>
        </Box>
        <Btn variant="amber" className="mt-3" onClick={() => navigate('/premium')}>Passa a Premium</Btn>
        <Footer><Btn variant="ghost" onClick={() => navigate('/sicurezza')}>◀ Sicurezza</Btn></Footer>
      </Frame>
    );
  }

  const modeLabel = mode === 'open' ? '🔓 aperta' : mode === 'whitelist' ? '🛡 whitelist' : '📬 richiesta';

  return (
    <Frame statusLeft="TUONO" statusRight="⚡">
      <Header title="Massima sicurezza ⚡" crumb="◀" onCrumbClick={() => navigate('/sicurezza')} />

      <Box variant="warn">
        <p className="text-[14px] text-ink">Per chi ha davvero bisogno di sparire</p>
        <p className="text-[13px] text-ink-dim mt-1">
          Tre passi: apri l'inbox, nascondi da dove ti colleghi, usa un'identità solo per questo.
          Non è la modalità di tutti i giorni.
        </p>
      </Box>

      {/* 1 · Inbox aperta */}
      <Box className="mt-3">
        <p className="text-ink">1 · Inbox aperta — stato: {modeLabel}</p>
        <p className="text-[13px] text-ink-dim mt-1">
          Chiunque può scriverti e la chat parte <b>subito</b>, senza "accetta".
          In cambio, ti possono scrivere anche sconosciuti.
        </p>
        {mode !== 'open' ? (
          <Btn variant="amber" className="mt-2" disabled={busy} onClick={() => applyMode('open')}>
            {busy ? '…' : 'Attiva inbox aperta'}
          </Btn>
        ) : (
          <Btn variant="ghost" className="mt-2" disabled={busy} onClick={() => applyMode('request')}>
            {busy ? '…' : 'Disattiva (torna a "richiesta")'}
          </Btn>
        )}
        {msg && <p className="text-[12px] text-ink-dim mt-1">{msg}</p>}
      </Box>

      {/* 2 · Tor */}
      <Box className="mt-3">
        <p className="text-ink">2 · Nascondi da dove ti colleghi (Tor)</p>
        <p className="text-[13px] text-ink-dim mt-1">
          Con Tor, nessuno può vedere da dove ti connetti.
        </p>
        <Btn variant="ghost" className="mt-2" onClick={() => navigate('/tor')}>🧅 Apri Identità Tor-only →</Btn>
      </Box>

      {/* Sealed-sender: mittente nascosto al server */}
      <Box className="mt-3">
        <p className="text-amber text-[15px] mb-1">🛡 Messaggi a mittente nascosto</p>
        <p className="text-[13px] text-ink-dim">
          Scrivi senza che <b className="text-ink">il server sappia che sei tu</b> a mandare.
          Meglio ancora da un'identità usata solo con Tor.
        </p>
        <Btn variant="ghost" className="mt-2" onClick={() => navigate('/sealed')}>💬 Conversazioni anonime →</Btn>
      </Box>

      {/* 3 · Identità dedicata */}
      <Box className="mt-3">
        <p className="text-ink">3 · Un'identità nuova, solo via Tor</p>
        <p className="text-[13px] text-ink-dim mt-1">
          Per sparire davvero, usa un'identità <b>mai aperta senza Tor</b>. Nullum tiene un'identità per
          dispositivo: per averne una separata, creane una nuova (su un altro dispositivo o da zero) e
          aprila <b>sempre dentro Tor</b>, fin dalla prima volta.
        </p>
      </Box>

      <Box variant="warn" className="mt-3">
        <p className="text-[12px] text-ink-dim">
          Più protezione, meno comodità: Tor rallenta, l'inbox aperta ti espone agli sconosciuti,
          un'identità nuova vuol dire ricominciare i contatti.
        </p>
      </Box>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/sicurezza')}>◀ Sicurezza</Btn>
      </Footer>
    </Frame>
  );
}
